import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";
import { API_URL } from "../../utils/config";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { validarEmail, validarTelefon } from "../../utils/Validacions";
import AjudaEntitats from "./AjudaEntitats";
import { BotoAjuda } from "./AjudaModal";

function idComercial(c) {
  return c ? Number(c.idComercials) : null;
}

function nomCompletComercial(c) {
  if (!c) return "";
  const cognoms = [c.Cognom1, c.Cognom2].filter((x) => x && String(x).trim()).join(" ");
  return [cognoms, c.Nom].filter((x) => x && String(x).trim()).join(", ");
}

function nomProveidor(proveidor) {
  if (!proveidor) return "";
  const tipus = mapToPresentable(proveidor.TipusEntitat, mapejos.tipusEntitat);
  const cognoms = [proveidor.Cognom1, proveidor.Cognom2].filter(Boolean).join(" ");
  if (["Empresa", "Autònom", "Autonom"].includes(tipus) || ["Empresa", "Autonom"].includes(proveidor.TipusEntitat)) {
    return proveidor.NomComercial?.trim() ? proveidor.NomComercial : proveidor.Nom;
  }
  return [cognoms, proveidor.Nom].filter((x) => x && String(x).trim()).join(", ");
}

function Comercialsproveidor({ proveidor, onCancel, subnav }) {
  const [activeTab, setActiveTab] = useState("table");
  const [comercials, setComercials] = useState([]);
  const [selected, setSelected] = useState(null);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const { rolUsuari } = useUserContext();
  const pucCrear = tincPermis(rolUsuari, "CREAR");
  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const handleEsc = useCallback(() => {
    if (activeTab === "table") onCancel?.();
    else setActiveTab("table");
  }, [activeTab, onCancel]);
  TeclaEscEnter(handleEsc, null);

  const fetchComercials = async () => {
    if (!proveidor?.idProveidors) {
      setComercials([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/Comercials.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idProveidors: proveidor.idProveidors }),
      });
      const data = await response.json();
      setComercials(data.success ? data.comercials || [] : []);
    } catch (error) {
      console.error("Error carregant comercials:", error);
      setComercials([]);
    }
  };

  useEffect(() => {
    setActiveTab("table");
    setSelected(null);
    fetchComercials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveidor?.idProveidors]);

  const reFetchToTable = () => {
    fetchComercials();
    setActiveTab("table");
    setSelected(null);
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  useEffect(() => {
    if (activeTab !== "consultar") setMenuAccions(false);
  }, [activeTab]);

  const obrirFitxa = (comercial, mode) => {
    setMenuObert(null);
    setSelected(comercial);
    setActiveTab(mode);
  };

  const obrirMenu = (e, comercial) => {
    e.stopPropagation();
    const id = idComercial(comercial);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 160;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const renderAccions = (comercial) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === idComercial(comercial) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, comercial)}
      >
        …
      </button>
    </div>
  );

  const files = comercials.map((c) => ({
    c,
    nomFila: nomCompletComercial(c),
    isSelected: idComercial(selected) === idComercial(c),
  }));

  const comercialMenu = menuObert ? files.find(({ c }) => idComercial(c) === menuObert)?.c : null;

  const renderTaula = () => (
    <section className="dv2-card">
      {files.length === 0 ? (
        <p className="dv2-empty">No hi ha empleats d'aquest proveïdor.</p>
      ) : isNarrow ? (
        <div className="dv2-recordList" style={{ display: "flex" }}>
          {files.map(({ c, nomFila, isSelected }) => (
            <article
              key={c.idComercials}
              className={`dv2-record ${isSelected ? "is-selected" : ""}`}
              onClick={() => setSelected(isSelected ? null : c)}
              onDoubleClick={() => pucConsultar && obrirFitxa(c, "consultar")}
            >
              <div>
                <p className="dv2-record-title">{nomFila || "—"}</p>
                <p className="dv2-record-meta">{[c.TelefonMobil, c.Email].filter(Boolean).join(" · ") || "—"}</p>
              </div>
              {renderAccions(c)}
            </article>
          ))}
        </div>
      ) : (
        <div className="dv2-tableWrap">
          <table className="dv2-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Telèfon</th>
                <th>Email</th>
                <th className="dv2-table-actions">Accions</th>
              </tr>
            </thead>
            <tbody>
              {files.map(({ c, nomFila, isSelected }) => (
                <tr
                  key={c.idComercials}
                  className={isSelected ? "is-selected" : ""}
                  onClick={() => setSelected(isSelected ? null : c)}
                  onDoubleClick={() => pucConsultar && obrirFitxa(c, "consultar")}
                >
                  <td>{nomFila || "—"}</td>
                  <td>{c.TelefonMobil || "—"}</td>
                  <td>{c.Email || "—"}</td>
                  <td className="dv2-table-actions">{renderAccions(c)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {comercialMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => obrirFitxa(comercialMenu, "consultar")}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => obrirFitxa(comercialMenu, "modificar")}>
                Modificar
              </button>
            )}
            {pucEsborrar && (
              <button type="button" className="dv2-menu-item is-danger" onClick={() => obrirFitxa(comercialMenu, "esborrar")}>
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </section>
  );

  const canNavigate = useMemo(() => comercials.length > 1 && !!selected, [comercials.length, selected]);
  const idxConsultar = selected ? comercials.findIndex((c) => idComercial(c) === idComercial(selected)) : -1;

  const goPrev = () => {
    if (!selected || comercials.length === 0) return;
    const idx = comercials.findIndex((c) => idComercial(c) === idComercial(selected));
    if (idx < 0) return;
    setSelected(comercials[idx === 0 ? comercials.length - 1 : idx - 1]);
  };
  const goNext = () => {
    if (!selected || comercials.length === 0) return;
    const idx = comercials.findIndex((c) => idComercial(c) === idComercial(selected));
    if (idx < 0) return;
    setSelected(comercials[idx === comercials.length - 1 ? 0 : idx + 1]);
  };

  const renderFitxa = (mode) => (
    <DetallsComercial
      mode={mode}
      proveidor={proveidor}
      comercial={mode === "nou" ? null : selected}
      onSave={(updated) => {
        if (mode === "nou") {
          reFetchToTable();
          return;
        }
        fetchComercials();
        if (updated) setSelected((prev) => ({ ...(prev || {}), ...updated }));
        setActiveTab("consultar");
      }}
      onDeleted={reFetchToTable}
      onCancel={() => setActiveTab("table")}
      onPrev={canNavigate ? goPrev : undefined}
      onNext={canNavigate ? goNext : undefined}
      navIndex={idxConsultar >= 0 ? idxConsultar + 1 : 0}
      navTotal={comercials.length}
    />
  );

  const titol =
    activeTab === "nou"
      ? "Nou empleat"
      : activeTab === "esborrar"
      ? "Esborrar empleat"
      : activeTab === "modificar"
      ? "Modificar empleat"
      : activeTab === "consultar"
      ? "Empleats · Fitxa"
      : "Empleats";

  return (
    <div className="dv2-entity">
      {mostraAjuda && <AjudaEntitats pestanya="proveidorEmpleats" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${activeTab === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {activeTab === "esborrar" ? "🗑️" : "👔"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titol}</h1>
            <p className="dv2-pageHeader-subtitle">{nomProveidor(proveidor) || "Comercials del proveïdor"}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funcionen els Empleats del proveïdor" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={activeTab === "table" ? onCancel : () => setActiveTab("table")}>
            Tornar
          </button>
          {activeTab === "table" && pucCrear && (
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              onClick={() => {
                setSelected(null);
                setActiveTab("nou");
              }}
            >
              Nou empleat
            </button>
          )}
          {activeTab === "consultar" && pucModificar && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => setActiveTab("modificar")}>
              Modificar
            </button>
          )}
          {activeTab === "consultar" && pucEsborrar && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      setActiveTab("esborrar");
                    }}
                  >
                    Esborrar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {subnav}
      {activeTab === "table" ? <div className="dv2-entity-body">{renderTaula()}</div> : renderFitxa(activeTab)}
    </div>
  );
}

function DetallsComercial({
  mode,
  comercial,
  proveidor,
  onSave,
  onDeleted,
  onCancel,
  onPrev,
  onNext,
  navIndex,
  navTotal,
}) {
  const isReadOnly = mode === "consultar" || mode === "esborrar";
  const [nom, setNom] = useState("");
  const [cognom1, setCognom1] = useState("");
  const [cognom2, setCognom2] = useState("");
  const [telefon, setTelefon] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (comercial && mode !== "nou") {
      setNom(comercial.Nom || "");
      setCognom1(comercial.Cognom1 || "");
      setCognom2(comercial.Cognom2 || "");
      setTelefon(comercial.TelefonMobil || "");
      setEmail(comercial.Email || "");
    } else {
      setNom("");
      setCognom1("");
      setCognom2("");
      setTelefon("");
      setEmail("");
    }
  }, [comercial, mode]);

  const handleSubmit = async () => {
    if (mode !== "esborrar") {
      if (!nom.trim() && !cognom1.trim() && !cognom2.trim()) {
        alert("Cal omplir almenys un d'aquests camps: Nom, Cognom 1 o Cognom 2.");
        return;
      }
      const tel = validarTelefon(telefon);
      if (!tel.ok) {
        alert(`Telèfon: ${tel.missatge}`);
        return;
      }
      const mail = validarEmail(email);
      if (!mail.ok) {
        alert(`Email: ${mail.missatge}`);
        return;
      }
    }

    const payload = {
      idComercials: comercial?.idComercials ?? null,
      Nom: nom.trim(),
      Cognom1: cognom1.trim(),
      Cognom2: cognom2.trim(),
      TelefonMobil: telefon.trim(),
      Email: email.trim(),
      Proveidors_idProveidors: proveidor?.idProveidors,
    };

    try {
      let url = "";
      if (mode === "nou") url = `${API_URL}/Noucomercial.php`;
      else if (mode === "modificar") url = `${API_URL}/Modificarcomercial.php`;
      else if (mode === "esborrar") url = `${API_URL}/Esborrarcomercial.php`;
      else {
        onCancel();
        return;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        if (mode === "esborrar") onDeleted?.();
        else onSave?.({ ...payload, idComercials: data.idComercials || payload.idComercials });
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error("Error gestió comercials:", error);
      alert("S'ha produït un error en el procés.");
    }
  };

  const camp = (label, required, content, mida = "mig") => {
    const extra =
      mida === "ample" ? "dv2-field--ample" : mida === "llarg" ? "dv2-field--llarg" : mida === "curt" ? "dv2-field--curt" : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className={`dv2-label ${required && !isReadOnly ? "dv2-required" : ""}`}>{label}</span>
        {content}
      </label>
    );
  };

  const nomComplet = nomCompletComercial({ Nom: nom, Cognom1: cognom1, Cognom2: cognom2 });
  const potNavegar = Boolean(onPrev && onNext && navTotal > 1);

  return (
    <>
      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">Aquesta acció no es pot desfer. S'esborra l'empleat d'aquest proveïdor.</div>
      )}
      <div className="dv2-entity-body">
        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">1</span>
            <h2 className="dv2-card-title">Dades de l'empleat</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {isReadOnly ? (
              <>
                {camp("Nom", false, <span className="dv2-value">{nomComplet || "—"}</span>, "ample")}
                {camp("Telèfon", false, <span className="dv2-value">{telefon || "—"}</span>)}
                {camp("Email", false, <span className="dv2-value">{email || "—"}</span>, "llarg")}
              </>
            ) : (
              <>
                {camp("Nom", false, <input className="dv2-input" type="text" value={nom} maxLength={45} autoFocus onChange={(e) => setNom(e.target.value)} />)}
                {camp("Cognom 1", false, <input className="dv2-input" type="text" value={cognom1} maxLength={45} onChange={(e) => setCognom1(e.target.value)} />)}
                {camp("Cognom 2", false, <input className="dv2-input" type="text" value={cognom2} maxLength={45} onChange={(e) => setCognom2(e.target.value)} />)}
                <span className="dv2-fields-break" aria-hidden="true" />
                {camp("Telèfon", false, <input className="dv2-input" type="text" value={telefon} maxLength={20} onChange={(e) => setTelefon(e.target.value)} />)}
                {camp("Email", false, <input className="dv2-input" type="email" value={email} maxLength={45} onChange={(e) => setEmail(e.target.value)} />, "llarg")}
              </>
            )}
          </div>
        </section>
      </div>
      {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={onPrev} title="Anterior">
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {navIndex} / {navTotal}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={onNext} title="Següent">
                ›
              </button>
            </div>
          )}
          {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel}>
                Cancel·lar
              </button>
              {mode === "esborrar" ? (
                <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                  Esborrar empleat
                </button>
              ) : (
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                  {mode === "nou" ? "Desar empleat" : "Desar"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default Comercialsproveidor;
